import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { useToast } from "../hooks/use-toast";
import {
  KeyRound, ShieldCheck, ShieldX, RefreshCw, Search, AlertCircle,
  Activity, Globe, Users, TrendingDown, MapPin, Monitor,
  CheckCircle2, XCircle, Ban, ChevronRight, Plane, UserX, Clock,
} from "lucide-react";
import { cn } from "../lib/utils";
import { fmtMDT } from "../lib/time";
import { useAuth } from "../context/AuthContext";

const OUTCOME_STYLE = {
  success:       { icon: CheckCircle2, cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300", label: "Success" },
  failure:       { icon: XCircle,      cls: "border-rose-500/30    bg-rose-500/10    text-rose-700    dark:text-rose-300",    label: "Failure" },
  blocked:       { icon: Ban,          cls: "border-orange-500/30  bg-orange-500/10  text-orange-700  dark:text-orange-300",  label: "Blocked" },
  mfa_challenge: { icon: ShieldCheck,  cls: "border-amber-500/30   bg-amber-500/10   text-amber-700   dark:text-amber-300",   label: "MFA" },
};

function fmtWhen(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 30 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return fmtMDT(iso);
}

export default function OnePassword() {
  const { user } = useAuth();
  const { toast } = useToast();

  const [status, setStatus] = useState(null);   // { configured, ok, features, account_uuid, users_api: { ok, member_count }}
  const [summary, setSummary] = useState(null); // KPIs
  const [signins, setSignins] = useState(null); // full signins payload
  const [audit, setAudit] = useState(null);
  const [fullRoster, setFullRoster] = useState(null); // Users API + events-enriched
  const [travel, setTravel] = useState(null);    // impossible-travel alerts
  const [staleReport, setStaleReport] = useState(null); // 30/60/90d bucketed
  const [days, setDays] = useState(7);
  const [query, setQuery] = useState("");
  const [stateFilter, setStateFilter] = useState("all"); // all | active | suspended | dormant
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("roster");

  const isAdmin = user?.role === "Super Admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const st = await api.get("/onepassword/events/status");
      setStatus(st.data);
      const eventsOk = st.data?.events?.ok !== false && st.data?.configured;
      const usersOk  = st.data?.users_api?.ok === true;
      const jobs = [];
      if (eventsOk) {
        jobs.push(api.get(`/onepassword/events/summary?days=${days}`).then((r) => setSummary(r.data)));
        jobs.push(api.get(`/onepassword/events/signins?days=${days}&limit=500`).then((r) => setSignins(r.data)));
        jobs.push(api.get(`/onepassword/events/audit?days=${days}&limit=200`).then((r) => setAudit(r.data)));
        jobs.push(api.get(`/onepassword/impossible-travel?days=${Math.max(days, 14)}`).then((r) => setTravel(r.data)).catch(() => setTravel(null)));
      } else {
        setSummary(null); setSignins(null); setAudit(null); setTravel(null);
      }
      if (usersOk) {
        jobs.push(api.get(`/onepassword/roster?days=${days}`).then((r) => setFullRoster(r.data)));
        jobs.push(api.get(`/onepassword/stale-report?lookback_days=90`).then((r) => setStaleReport(r.data)).catch(() => setStaleReport(null)));
      } else {
        setFullRoster(null); setStaleReport(null);
      }
      await Promise.all(jobs);
    } catch (e) {
      toast({ title: "Couldn't load 1Password data", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setLoading(false); }
  }, [days, toast]);

  useEffect(() => { load(); }, [load]);

  const sum = summary?.summary || signins?.summary || {};
  // Prefer the full Users-API roster (25 members) when we have it; fall
  // back to the events-derived roster (only members who signed in) otherwise.
  const roster = useMemo(
    () => fullRoster?.roster || signins?.roster || [],
    [fullRoster, signins]
  );
  const usingFullRoster = !!fullRoster;

  const filteredRoster = useMemo(() => {
    const q = query.trim().toLowerCase();
    return roster.filter((r) => {
      if (stateFilter === "active"    && r.state !== "active") return false;
      if (stateFilter === "suspended" && r.state !== "suspended") return false;
      if (stateFilter === "dormant"   && (r.has_events !== false || r.state !== "active")) return false;
      if (!q) return true;
      return (r.name || "").toLowerCase().includes(q) || (r.email || "").toLowerCase().includes(q);
    });
  }, [roster, query, stateFilter]);

  // ---- Drill-down state ----
  const [detailMember, setDetailMember] = useState(null); // { email, uuid, name, ... } or a key we lookup
  const [signinFilter, setSigninFilter] = useState(null); // "failure" | "success" | null
  const [countryFilter, setCountryFilter] = useState(null);

  const openMember = useCallback((identifier) => {
    // Accepts a roster row, a sign-in event's user, an audit event's actor, etc.
    const email = (identifier?.email || identifier?.user?.email || identifier?.actor?.email || "").toLowerCase();
    if (!email) return;
    // Prefer the full-roster row (has state + created_at + last_seen).
    const rosterRow = roster.find((r) => (r.email || "").toLowerCase() === email);
    setDetailMember(rosterRow || { email, name: identifier?.name || identifier?.user?.name || identifier?.actor?.name });
  }, [roster]);

  const memberSignins = useMemo(() => {
    if (!detailMember) return [];
    const em = (detailMember.email || "").toLowerCase();
    return (signins?.signins || []).filter((s) => (s.user?.email || "").toLowerCase() === em);
  }, [detailMember, signins]);

  const memberAudit = useMemo(() => {
    if (!detailMember) return [];
    const em = (detailMember.email || "").toLowerCase();
    return (audit?.audit || []).filter((a) => (a.actor?.email || "").toLowerCase() === em);
  }, [detailMember, audit]);

  const memberTravelAlerts = useMemo(() => {
    if (!detailMember) return [];
    const em = (detailMember.email || "").toLowerCase();
    return (travel?.alerts || []).filter((a) => (a.user?.email || "").toLowerCase() === em);
  }, [detailMember, travel]);

  // Client-side signin filter (applied when a KPI tile switches to the signins tab).
  const displayedSignins = useMemo(() => {
    const rows = signins?.signins || [];
    if (!signinFilter && !countryFilter) return rows;
    return rows.filter((s) => {
      if (signinFilter === "failure" && s.outcome !== "failure" && s.outcome !== "blocked") return false;
      if (signinFilter === "success" && s.outcome !== "success") return false;
      if (countryFilter && s.country !== countryFilter) return false;
      return true;
    });
  }, [signins, signinFilter, countryFilter]);

  const configured = status?.configured;
  const live = configured && status?.ok !== false;

  return (
    <div className="space-y-5" data-testid="onepassword-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 text-primary text-[12px] font-semibold uppercase tracking-wider">
            <KeyRound className="w-3.5 h-3.5" /> 1Password
          </div>
          <h1 className="text-[26px] font-semibold tracking-tight mt-1">User activity & audit</h1>
          <p className="text-[13px] text-muted-foreground max-w-2xl mt-1">
            {live
              ? "Live Events API feed — sign-in attempts, audit history, and a derived roster of active team members."
              : "1Password Events API not yet configured. Set ONEPASSWORD_EVENTS_TOKEN in backend/.env to enable."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {configured && (live ? (
            <span data-testid="op-mode-badge" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-[11.5px] font-semibold px-2.5 py-1 uppercase tracking-wide">
              <ShieldCheck className="w-3.5 h-3.5" /> LIVE · Events API
            </span>
          ) : (
            <span data-testid="op-mode-badge" className="inline-flex items-center gap-1.5 rounded-full border border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300 text-[11.5px] font-semibold px-2.5 py-1 uppercase tracking-wide">
              <AlertCircle className="w-3.5 h-3.5" /> Bridge unreachable
            </span>
          ))}
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="h-9 rounded-md border border-border bg-background text-[13px] px-2"
            data-testid="op-window-select"
          >
            <option value={1}>Last 24h</option>
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </select>
          <Button variant="outline" size="sm" onClick={load} className="gap-1.5" data-testid="op-refresh-btn">
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} /> Refresh
          </Button>
        </div>
      </div>

      {/* Not-configured state */}
      {!configured && (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 text-[13px]">
          <div className="font-semibold text-[14px] mb-1 inline-flex items-center gap-1.5">
            <AlertCircle className="w-4 h-4 text-primary" /> One-time setup
          </div>
          <ol className="list-decimal ml-5 space-y-1 text-muted-foreground">
            <li>Sign into <a href="https://start.1password.com/" target="_blank" rel="noreferrer" className="text-primary hover:underline">1Password admin</a> as an Owner.</li>
            <li><strong>Integrations → Events Reporting → Add integration</strong> (scopes: sign-ins, item usage, audit).</li>
            <li>Copy the bearer token (shown once) and set <code className="bg-muted px-1 rounded">ONEPASSWORD_EVENTS_TOKEN</code> in <code className="bg-muted px-1 rounded">backend/.env</code>.</li>
            <li>Restart the backend — this page will flip to LIVE automatically.</li>
          </ol>
        </div>
      )}

      {/* KPI row */}
      {live && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="op-kpis">
          <Kpi
            icon={Users}
            label={usingFullRoster ? "Total members" : "Active members"}
            value={usingFullRoster ? (fullRoster?.count ?? 0) : (sum.unique_users ?? 0)}
            sub={usingFullRoster
              ? `${fullRoster?.states?.active ?? 0} active · ${fullRoster?.states?.suspended ?? 0} suspended`
              : `In last ${days}d`}
            tone="primary"
            onClick={() => { setTab("roster"); setStateFilter("all"); setQuery(""); }}
          />
          <Kpi
            icon={AlertCircle}
            label="Dormant"
            value={usingFullRoster ? (fullRoster?.stale?.never ?? 0) : "—"}
            sub={usingFullRoster ? `No sign-in in ${days}d` : "Enable Users API"}
            tone={usingFullRoster && (fullRoster?.stale?.never ?? 0) > 0 ? "rose" : "emerald"}
            onClick={usingFullRoster ? () => { setTab("roster"); setStateFilter("dormant"); setQuery(""); } : undefined}
          />
          <Kpi
            icon={Plane}
            label="Travel alerts"
            value={travel?.count ?? 0}
            sub={travel ? `Scanned ${travel.signins_scanned} sign-ins` : "—"}
            tone={(travel?.count ?? 0) > 0 ? "rose" : "emerald"}
            onClick={() => setTab("travel")}
          />
          <Kpi
            icon={Activity}
            label="Sign-ins"
            value={sum.signins_total ?? 0}
            sub={`${sum.signins_success ?? 0} success`}
            tone="primary"
            onClick={() => { setTab("signins"); setSigninFilter(null); setCountryFilter(null); }}
          />
          <Kpi
            icon={TrendingDown}
            label="Failure rate"
            value={`${sum.failure_pct ?? 0}%`}
            sub={`${sum.signins_failure ?? 0} failed`}
            tone={(sum.failure_pct ?? 0) > 5 ? "rose" : "emerald"}
            onClick={(sum.signins_failure ?? 0) > 0 ? () => { setTab("signins"); setSigninFilter("failure"); setCountryFilter(null); } : undefined}
          />
          <Kpi
            icon={Globe}
            label="Top region"
            value={sum.top_country || "—"}
            sub="Most sign-ins"
            tone="primary"
            onClick={sum.top_country ? () => { setTab("signins"); setCountryFilter(sum.top_country); setSigninFilter(null); } : undefined}
          />
        </div>
      )}

      {/* Tabs */}
      {live && (
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList data-testid="op-tabs-list">
            <TabsTrigger value="roster" data-testid="op-tab-roster">Active roster</TabsTrigger>
            <TabsTrigger value="signins" data-testid="op-tab-signins">Sign-in activity</TabsTrigger>
            <TabsTrigger value="audit" data-testid="op-tab-audit">Audit events</TabsTrigger>
            <TabsTrigger value="travel" data-testid="op-tab-travel">
              Impossible travel
              {(travel?.count ?? 0) > 0 && (
                <span className="ml-1.5 text-[9.5px] rounded-full bg-rose-500 text-white px-1.5 py-0.5 font-semibold tabular-nums">{travel.count}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="stale" data-testid="op-tab-stale">Stale users</TabsTrigger>
          </TabsList>

          <TabsContent value="roster" className="mt-3 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter by name or email…" className="pl-9" data-testid="op-search-input" />
              </div>
              {usingFullRoster && (
                <div className="flex items-center gap-1">
                  {[
                    { key: "all",       label: `All ${fullRoster.count}` },
                    { key: "active",    label: `Active ${fullRoster.states.active}` },
                    { key: "suspended", label: `Suspended ${fullRoster.states.suspended}` },
                    { key: "dormant",   label: `Dormant ${fullRoster.stale.never}` },
                  ].map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setStateFilter(c.key)}
                      data-testid={`op-state-${c.key}`}
                      className={cn(
                        "text-[10.5px] rounded-full border px-2 py-1 font-semibold uppercase tracking-wide transition-colors",
                        stateFilter === c.key
                          ? "border-primary/40 bg-primary/10 text-primary"
                          : "border-border text-muted-foreground hover:bg-muted/50"
                      )}
                    >{c.label}</button>
                  ))}
                </div>
              )}
            </div>
            <RosterTable rows={filteredRoster} loading={loading} usingFullRoster={usingFullRoster} onOpen={openMember} />
          </TabsContent>

          <TabsContent value="signins" className="mt-3 space-y-2">
            {(signinFilter || countryFilter) && (
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="op-signin-active-filters">
                <span className="text-[11px] text-muted-foreground">Filtered:</span>
                {signinFilter && (
                  <button
                    type="button"
                    onClick={() => setSigninFilter(null)}
                    className="text-[10.5px] rounded-full border border-primary/40 bg-primary/10 text-primary px-2 py-0.5 font-semibold uppercase tracking-wide hover:bg-primary/20"
                    data-testid="op-clear-signin-filter"
                  >
                    {signinFilter} ✕
                  </button>
                )}
                {countryFilter && (
                  <button
                    type="button"
                    onClick={() => setCountryFilter(null)}
                    className="text-[10.5px] rounded-full border border-primary/40 bg-primary/10 text-primary px-2 py-0.5 font-semibold uppercase tracking-wide hover:bg-primary/20"
                    data-testid="op-clear-country-filter"
                  >
                    {countryFilter} ✕
                  </button>
                )}
                <span className="text-[10.5px] text-muted-foreground ml-1">{displayedSignins.length} shown</span>
              </div>
            )}
            <SigninsTable rows={displayedSignins} loading={loading} onOpen={openMember} />
          </TabsContent>

          <TabsContent value="audit" className="mt-3">
            <AuditTable rows={audit?.audit || []} loading={loading} onOpen={openMember} />
          </TabsContent>

          <TabsContent value="travel" className="mt-3">
            <TravelTable alerts={travel?.alerts || []} loading={loading} scanned={travel?.signins_scanned} onOpen={openMember} />
          </TabsContent>

          <TabsContent value="stale" className="mt-3">
            <StaleReport report={staleReport} loading={loading} onOpen={openMember} />
          </TabsContent>
        </Tabs>
      )}

      <MemberDetailModal
        member={detailMember}
        open={!!detailMember}
        onClose={() => setDetailMember(null)}
        signins={memberSignins}
        audit={memberAudit}
        travelAlerts={memberTravelAlerts}
      />
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, tone = "primary", onClick }) {
  const cls = {
    primary: "border-primary/30 bg-primary/5 text-primary",
    rose:    "border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-300",
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300",
  }[tone] || "border-border bg-card";
  const clickable = typeof onClick === "function";
  const Cmp = clickable ? "button" : "div";
  return (
    <Cmp
      type={clickable ? "button" : undefined}
      onClick={clickable ? onClick : undefined}
      className={cn(
        "rounded-xl border p-4 flex items-start gap-3 text-left w-full transition-all",
        cls,
        clickable && "hover:brightness-110 hover:-translate-y-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/40"
      )}
    >
      <div className="w-9 h-9 rounded-lg bg-background/60 border border-current/20 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
        <div className="text-[22px] font-bold leading-none mt-1 tabular-nums">{value}</div>
        {sub && <div className="text-[11px] opacity-70 mt-1">{sub}</div>}
      </div>
    </Cmp>
  );
}

function RosterTable({ rows, loading, usingFullRoster, onOpen }) {
  if (loading && rows.length === 0) {
    return <div className="space-y-2">{[...Array(6)].map((_, i) => <div key={i} className="h-14 rounded-md bg-muted/20 animate-pulse" />)}</div>;
  }
  if (!rows.length) return <EmptyState label="No members match this filter." />;
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="op-roster-table">
      <div className="grid grid-cols-12 gap-3 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground border-b border-border bg-muted/40">
        <div className="col-span-4">Member</div>
        <div className="col-span-2">State</div>
        <div className="col-span-2">Last seen</div>
        <div className="col-span-2">Last device / geo</div>
        <div className="col-span-1">Sessions</div>
        <div className="col-span-1 text-right">Health</div>
      </div>
      <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
        {rows.map((r) => {
          const dormant = usingFullRoster && r.has_events === false && r.state === "active";
          const stateBadge = {
            active:    { cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300", label: "Active" },
            suspended: { cls: "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300", label: "Suspended" },
            pending:   { cls: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300", label: "Pending" },
          }[r.state] || { cls: "border-border bg-muted/40 text-muted-foreground", label: r.state || "Unknown" };
          return (
            <button
              key={r.uuid}
              type="button"
              onClick={() => onOpen?.(r)}
              data-testid={`op-roster-row-${r.uuid}`}
              className={cn(
                "grid grid-cols-12 gap-3 items-center px-4 py-3 hover:bg-primary/5 hover:border-l-2 hover:border-l-primary text-left w-full transition-colors focus:outline-none focus:bg-primary/5",
                dormant && "bg-amber-500/5"
              )}
            >
              <div className="col-span-4 min-w-0">
                <div className="font-semibold text-[13.5px] truncate">{r.name || "—"}</div>
                <div className="text-[12px] text-muted-foreground truncate">{r.email}</div>
              </div>
              <div className="col-span-2">
                <span className={cn("inline-flex items-center rounded-full border text-[10.5px] font-semibold px-2 py-0.5 uppercase tracking-wide", stateBadge.cls)}>
                  {stateBadge.label}
                </span>
                {dormant && (
                  <div className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold mt-1 uppercase tracking-wide">
                    Dormant · no recent sign-in
                  </div>
                )}
              </div>
              <div className="col-span-2 text-[12.5px]">
                {r.last_seen ? (
                  <>
                    <div>{fmtWhen(r.last_seen)}</div>
                    <div className="text-[10.5px] text-muted-foreground">{fmtMDT(r.last_seen)}</div>
                  </>
                ) : (
                  <span className="text-[11.5px] text-muted-foreground italic">
                    {r.created_at ? `Never in window · joined ${fmtMDT(r.created_at)}` : "Never"}
                  </span>
                )}
              </div>
              <div className="col-span-2 min-w-0">
                {r.last_ip ? (
                  <>
                    <div className="text-[12px] truncate inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-muted-foreground" />
                      {r.last_geo?.city || "—"}{r.last_geo?.country ? `, ${r.last_geo.country}` : ""}
                    </div>
                    <div className="text-[10.5px] text-muted-foreground truncate">{r.last_app} · {r.last_os}</div>
                  </>
                ) : (
                  <span className="text-[11.5px] text-muted-foreground">—</span>
                )}
              </div>
              <div className="col-span-1 text-[12.5px] tabular-nums">
                <div className="font-semibold">{r.sessions_seen}</div>
                {(r.blocked > 0 || r.failures > 0) && (
                  <div className="text-[10.5px]">
                    {r.blocked ? <span className="text-orange-500">{r.blocked}b </span> : null}
                    {r.failures ? <span className="text-rose-500">{r.failures}f</span> : null}
                  </div>
                )}
              </div>
              <div className="col-span-1 text-right">
                {r.state === "suspended"
                  ? <span className="inline-flex items-center rounded-full border border-slate-500/30 bg-slate-500/10 text-slate-600 dark:text-slate-400 text-[10.5px] font-semibold px-2 py-0.5">Off</span>
                  : dormant
                  ? <span className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-[10.5px] font-semibold px-2 py-0.5">Dormant</span>
                  : r.blocked > 0 || r.failures > 2
                  ? <span className="inline-flex items-center rounded-full border border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300 text-[10.5px] font-semibold px-2 py-0.5">At risk</span>
                  : <span className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-[10.5px] font-semibold px-2 py-0.5">Healthy</span>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SigninsTable({ rows, loading, onOpen }) {
  if (loading && rows.length === 0) {
    return <div className="space-y-2">{[...Array(6)].map((_, i) => <div key={i} className="h-12 rounded-md bg-muted/20 animate-pulse" />)}</div>;
  }
  if (!rows.length) return <EmptyState label="No sign-in events match this filter." />;
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="op-signins-table">
      <div className="grid grid-cols-12 gap-3 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground border-b border-border bg-muted/40">
        <div className="col-span-2">When</div>
        <div className="col-span-3">Who</div>
        <div className="col-span-2">Outcome</div>
        <div className="col-span-3">Where</div>
        <div className="col-span-2">Client</div>
      </div>
      <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
        {rows.map((s) => {
          const out = OUTCOME_STYLE[s.outcome] || OUTCOME_STYLE.failure;
          const Icon = out.icon;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onOpen?.(s)}
              className="grid grid-cols-12 gap-3 items-center px-4 py-2.5 hover:bg-primary/5 hover:border-l-2 hover:border-l-primary text-[12px] text-left w-full transition-colors focus:outline-none focus:bg-primary/5"
              data-testid={`op-signin-${s.id}`}
            >
              <div className="col-span-2">
                <div>{fmtWhen(s.timestamp)}</div>
                <div className="text-[10.5px] text-muted-foreground">{fmtMDT(s.timestamp)}</div>
              </div>
              <div className="col-span-3 min-w-0">
                <div className="font-medium truncate">{s.user?.name || "—"}</div>
                <div className="text-[10.5px] text-muted-foreground truncate">{s.user?.email}</div>
              </div>
              <div className="col-span-2">
                <span className={cn("inline-flex items-center gap-1 rounded-full border text-[10.5px] font-semibold px-2 py-0.5 uppercase tracking-wide", out.cls)}>
                  <Icon className="w-3 h-3" /> {out.label}
                </span>
                <div className="text-[10.5px] text-muted-foreground mt-0.5">{s.category} · {s.type}</div>
              </div>
              <div className="col-span-3 min-w-0">
                <div className="truncate">{[s.city, s.region, s.country].filter(Boolean).join(", ") || "—"}</div>
                <div className="text-[10.5px] text-muted-foreground font-mono truncate">{s.ip}</div>
              </div>
              <div className="col-span-2 min-w-0">
                <div className="truncate">{s.app || "—"}</div>
                <div className="text-[10.5px] text-muted-foreground truncate">{s.os} {s.os_version}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AuditTable({ rows, loading, onOpen }) {
  if (loading && rows.length === 0) {
    return <div className="space-y-2">{[...Array(6)].map((_, i) => <div key={i} className="h-12 rounded-md bg-muted/20 animate-pulse" />)}</div>;
  }
  if (!rows.length) return <EmptyState label="No audit events in this window." />;
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="op-audit-table">
      <div className="grid grid-cols-12 gap-3 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground border-b border-border bg-muted/40">
        <div className="col-span-2">When</div>
        <div className="col-span-3">Actor</div>
        <div className="col-span-3">Action</div>
        <div className="col-span-2">Object</div>
        <div className="col-span-2">Where</div>
      </div>
      <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
        {rows.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => a.actor?.email && onOpen?.(a)}
            className={cn(
              "grid grid-cols-12 gap-3 items-center px-4 py-2.5 text-[12px] text-left w-full transition-colors",
              a.actor?.email
                ? "hover:bg-primary/5 hover:border-l-2 hover:border-l-primary focus:outline-none focus:bg-primary/5 cursor-pointer"
                : "cursor-default"
            )}
            data-testid={`op-audit-${a.id}`}
          >
            <div className="col-span-2">
              <div>{fmtWhen(a.timestamp)}</div>
              <div className="text-[10.5px] text-muted-foreground">{fmtMDT(a.timestamp)}</div>
            </div>
            <div className="col-span-3 min-w-0">
              <div className="font-medium truncate">{a.actor?.name || "—"}</div>
              <div className="text-[10.5px] text-muted-foreground truncate">{a.actor?.email}</div>
            </div>
            <div className="col-span-3 min-w-0">
              <span className="inline-flex items-center rounded border border-border bg-muted/50 text-[10.5px] font-mono px-1.5 py-0.5">{a.action}</span>
              <span className="ml-1.5 text-[10.5px] text-muted-foreground">on {a.object_type}</span>
              <div className="text-[10.5px] text-muted-foreground font-mono truncate mt-0.5">{a.object_uuid}</div>
            </div>
            <div className="col-span-2 min-w-0">
              <div className="text-[10.5px] font-mono truncate">{a.object_uuid}</div>
              <div className="text-[10.5px] text-muted-foreground">{a.object_type}</div>
            </div>
            <div className="col-span-2 min-w-0">
              <div className="truncate">{[a.city, a.country].filter(Boolean).join(", ") || "—"}</div>
              <div className="text-[10.5px] text-muted-foreground font-mono truncate">{a.ip}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-muted/20 py-12 text-center text-[13px] text-muted-foreground">
      {label}
    </div>
  );
}

function TravelTable({ alerts, loading, scanned, onOpen }) {
  if (loading && alerts.length === 0) {
    return <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-16 rounded-md bg-muted/20 animate-pulse" />)}</div>;
  }
  if (!alerts.length) {
    return (
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 py-10 text-center">
        <ShieldCheck className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
        <div className="text-[14px] font-semibold text-emerald-700 dark:text-emerald-300">No impossible travel detected</div>
        <div className="text-[12px] text-muted-foreground mt-1">Scanned {scanned ?? 0} sign-in{scanned === 1 ? "" : "s"}. Every consecutive sign-in pair was geographically plausible.</div>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="op-travel-list">
      <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 text-[13px] text-rose-700 dark:text-rose-300">
        <strong>{alerts.length} alert{alerts.length === 1 ? "" : "s"}.</strong> Sign-in pairs implying travel faster than a commercial flight — usually VPN/proxy switching, but always worth a look. Click any alert to see the member's full history.
      </div>
      {alerts.map((a, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onOpen?.(a)}
          data-testid={`op-travel-alert-${i}`}
          className={cn(
            "rounded-xl border p-4 space-y-3 text-left w-full transition-all hover:brightness-110 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary/40 cursor-pointer",
            a.severity === "high" ? "border-rose-500/30 bg-rose-500/5" : "border-amber-500/30 bg-amber-500/5"
          )}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="font-semibold text-[14px] truncate">{a.user?.name || "Unknown"}</div>
              <div className="text-[11.5px] text-muted-foreground truncate">{a.user?.email}</div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className={cn(
                "inline-flex items-center gap-1 rounded-full border text-[10.5px] font-semibold px-2 py-0.5 uppercase tracking-wide",
                a.severity === "high"
                  ? "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300"
                  : "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
              )}>
                <AlertCircle className="w-3 h-3" /> {a.severity}
              </span>
              {a.cross_country && (
                <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 text-primary text-[10.5px] font-semibold px-2 py-0.5 uppercase tracking-wide">
                  <Globe className="w-3 h-3" /> Intl
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TravelPoint label="From" pt={a.from} />
            <TravelPoint label="To" pt={a.to} />
          </div>

          <div className="grid grid-cols-3 gap-2 text-center bg-background/50 rounded-lg p-2 border border-border">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Distance</div>
              <div className="text-[15px] font-bold tabular-nums">{a.distance_km.toLocaleString()} km</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Gap</div>
              <div className="text-[15px] font-bold tabular-nums">{a.gap_hours} h</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Velocity</div>
              <div className="text-[15px] font-bold tabular-nums text-rose-600 dark:text-rose-400">{a.velocity_kmh.toLocaleString()} km/h</div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function TravelPoint({ label, pt }) {
  return (
    <div className="rounded-lg border border-border bg-background/50 p-2.5">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
      <div className="text-[13px] font-medium mt-0.5 inline-flex items-center gap-1">
        <MapPin className="w-3 h-3 text-muted-foreground" />
        {[pt.city, pt.region, pt.country].filter(Boolean).join(", ") || "Unknown location"}
      </div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{fmtMDT(pt.timestamp)}</div>
      <div className="text-[10.5px] text-muted-foreground font-mono truncate">{pt.ip}</div>
      <div className="text-[10.5px] text-muted-foreground truncate">{pt.app} · {pt.os}</div>
    </div>
  );
}

function StaleReport({ report, loading, onOpen }) {
  if (loading && !report) {
    return <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-14 rounded-md bg-muted/20 animate-pulse" />)}</div>;
  }
  if (!report) return <EmptyState label="Stale report needs the Users API — configure ONEPASSWORD_USERS_CLIENT_ID/SECRET." />;

  const buckets = [
    { key: "stale_90d", label: "≥ 90 days silent", tone: "rose",    icon: UserX,  hint: "Recommend off-boarding and license reclaim." },
    { key: "stale_60d", label: "60 – 90 days",    tone: "orange", icon: Clock,  hint: "Reach out to confirm they still need access." },
    { key: "stale_30d", label: "30 – 60 days",    tone: "amber",  icon: Clock,  hint: "Watch — may be on vacation or reduced use." },
    { key: "never",     label: "Never signed in", tone: "primary", icon: UserX, hint: `Never observed in the ${report.lookback_days}d window — could be pending or a service account.` },
  ];
  const totalStale = buckets.reduce((sum, b) => sum + (report[b.key]?.length || 0), 0);

  return (
    <div className="space-y-4" data-testid="op-stale-report">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StaleKpi label="Active recent" value={report.active_recent_count} tone="emerald" />
        <StaleKpi label="30 – 60 d" value={report.stale_30d.length} tone="amber" />
        <StaleKpi label="60 – 90 d" value={report.stale_60d.length} tone="orange" />
        <StaleKpi label="≥ 90 d"    value={report.stale_90d.length} tone="rose" />
        <StaleKpi label="Never"     value={report.never.length}     tone="primary" />
      </div>

      {totalStale === 0 ? (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 py-10 text-center">
          <ShieldCheck className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
          <div className="text-[14px] font-semibold text-emerald-700 dark:text-emerald-300">No stale users detected</div>
          <div className="text-[12px] text-muted-foreground mt-1">Every active member has signed in within the last {report.lookback_days} days.</div>
        </div>
      ) : (
        buckets.map((b) => {
          const rows = report[b.key] || [];
          if (!rows.length) return null;
          const Icon = b.icon;
          const toneCls = {
            rose:    "border-rose-500/30 bg-rose-500/5",
            orange:  "border-orange-500/30 bg-orange-500/5",
            amber:   "border-amber-500/30 bg-amber-500/5",
            primary: "border-primary/30 bg-primary/5",
          }[b.tone];
          return (
            <div key={b.key} className={cn("rounded-xl border p-4", toneCls)} data-testid={`op-stale-bucket-${b.key}`}>
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <div className="inline-flex items-center gap-2 min-w-0">
                  <Icon className="w-4 h-4 shrink-0" />
                  <div>
                    <div className="text-[13px] font-semibold">{b.label} <span className="text-muted-foreground font-normal">· {rows.length} member{rows.length === 1 ? "" : "s"}</span></div>
                    <div className="text-[11px] text-muted-foreground">{b.hint}</div>
                  </div>
                </div>
              </div>
              <div className="space-y-1">
                {rows.map((r) => (
                  <button
                    key={r.uuid}
                    type="button"
                    onClick={() => onOpen?.(r)}
                    className="flex items-center justify-between gap-3 bg-background/50 border border-border rounded-md px-3 py-2 w-full text-left hover:bg-primary/5 hover:border-primary/30 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40"
                    data-testid={`op-stale-row-${r.uuid}`}
                  >
                    <div className="min-w-0">
                      <div className="text-[13px] font-medium truncate">{r.name}</div>
                      <div className="text-[11.5px] text-muted-foreground truncate">{r.email}</div>
                    </div>
                    <div className="text-right shrink-0 min-w-[130px]">
                      <div className="text-[11.5px]">
                        {r.last_seen ? <>Last · {fmtMDT(r.last_seen)}</> : "No sign-in observed"}
                      </div>
                      <div className="text-[10.5px] text-muted-foreground">Joined {fmtMDT(r.created_at)}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

function StaleKpi({ label, value, tone }) {
  const cls = {
    emerald: "border-emerald-500/30 bg-emerald-500/5  text-emerald-700 dark:text-emerald-300",
    amber:   "border-amber-500/30  bg-amber-500/5   text-amber-700   dark:text-amber-300",
    orange:  "border-orange-500/30 bg-orange-500/5  text-orange-700  dark:text-orange-300",
    rose:    "border-rose-500/30   bg-rose-500/5    text-rose-700    dark:text-rose-300",
    primary: "border-primary/30    bg-primary/5     text-primary",
  }[tone];
  return (
    <div className={cn("rounded-xl border p-3 text-center", cls)}>
      <div className="text-[10.5px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
      <div className="text-[22px] font-bold tabular-nums leading-none mt-1">{value}</div>
    </div>
  );
}

function MemberDetailModal({ member, open, onClose, signins, audit, travelAlerts }) {
  if (!member) return null;

  const state = (member.state || "").toLowerCase();
  const stateBadge = {
    active:    { cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300", label: "Active" },
    suspended: { cls: "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300", label: "Suspended" },
    pending:   { cls: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300", label: "Pending" },
  }[state] || { cls: "border-border bg-muted/40 text-muted-foreground", label: state || "Unknown" };

  const outcomeCounts = signins.reduce((acc, s) => {
    acc[s.outcome || "failure"] = (acc[s.outcome || "failure"] || 0) + 1;
    return acc;
  }, {});

  const uniqueCountries = [...new Set(signins.map((s) => s.country).filter(Boolean))];
  const uniqueCities = [...new Set(signins.map((s) => s.city).filter(Boolean))];
  const uniqueIps = [...new Set(signins.map((s) => s.ip).filter(Boolean))];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col p-0" data-testid="op-member-detail-modal">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
          <DialogTitle className="flex items-center gap-2 pr-6 flex-wrap">
            <Users className="w-4 h-4 text-primary shrink-0" />
            <span className="truncate">{member.name || member.email}</span>
            <span className={cn("inline-flex items-center rounded-full border text-[10.5px] font-semibold px-2 py-0.5 uppercase tracking-wide", stateBadge.cls)}>
              {stateBadge.label}
            </span>
            {member.has_events === false && state === "active" && (
              <span className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-[10.5px] font-semibold px-2 py-0.5 uppercase tracking-wide">
                Dormant
              </span>
            )}
            {travelAlerts.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full border border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300 text-[10.5px] font-semibold px-2 py-0.5 uppercase tracking-wide">
                <Plane className="w-3 h-3" /> {travelAlerts.length} travel {travelAlerts.length === 1 ? "alert" : "alerts"}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-[12.5px]">{member.email}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Profile summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <MiniStat label="Joined"    value={member.created_at ? fmtMDT(member.created_at) : "—"} />
            <MiniStat label="Last seen" value={member.last_seen  ? fmtWhen(member.last_seen) : "Never in window"} />
            <MiniStat label="Sessions"  value={signins.length} />
            <MiniStat label="Failures"  value={(outcomeCounts.failure || 0) + (outcomeCounts.blocked || 0)} tone={((outcomeCounts.failure || 0) + (outcomeCounts.blocked || 0)) > 0 ? "rose" : "primary"} />
          </div>

          {/* Recent geo footprint */}
          {(uniqueCountries.length > 0 || uniqueCities.length > 0) && (
            <div className="rounded-lg border border-border p-3">
              <div className="text-[10.5px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Geo footprint</div>
              <div className="text-[12.5px] space-y-1">
                {uniqueCountries.length > 0 && (
                  <div><strong>Countries:</strong> {uniqueCountries.join(", ")}</div>
                )}
                {uniqueCities.length > 0 && (
                  <div><strong>Cities:</strong> {uniqueCities.slice(0, 8).join(", ")}{uniqueCities.length > 8 ? ` +${uniqueCities.length - 8}` : ""}</div>
                )}
                {uniqueIps.length > 0 && (
                  <div className="text-muted-foreground"><strong>IPs seen:</strong> <span className="font-mono">{uniqueIps.slice(0, 4).join(", ")}{uniqueIps.length > 4 ? ` +${uniqueIps.length - 4}` : ""}</span></div>
                )}
              </div>
            </div>
          )}

          {/* Travel alerts for this member */}
          {travelAlerts.length > 0 && (
            <section>
              <div className="text-[12.5px] font-semibold mb-1.5 inline-flex items-center gap-1.5">
                <Plane className="w-3.5 h-3.5 text-rose-500" />
                Travel alerts ({travelAlerts.length})
              </div>
              <div className="space-y-1.5">
                {travelAlerts.map((a, i) => (
                  <div key={i} className={cn(
                    "rounded-md border p-2 text-[12px]",
                    a.severity === "high" ? "border-rose-500/30 bg-rose-500/5" : "border-amber-500/30 bg-amber-500/5"
                  )}>
                    <div className="font-medium">
                      {a.from?.city || "—"} → {a.to?.city || "—"}
                      <span className="text-muted-foreground ml-1">· {a.distance_km.toLocaleString()} km in {a.gap_hours}h</span>
                      <span className="text-rose-600 dark:text-rose-400 ml-1 font-bold">→ {a.velocity_kmh.toLocaleString()} km/h</span>
                    </div>
                    <div className="text-[10.5px] text-muted-foreground">{fmtMDT(a.from?.timestamp)} → {fmtMDT(a.to?.timestamp)}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Sign-ins for this member */}
          <section>
            <div className="text-[12.5px] font-semibold mb-1.5">Recent sign-ins ({signins.length})</div>
            {signins.length === 0 ? (
              <div className="rounded-md border border-dashed border-border p-4 text-center text-[12px] text-muted-foreground">
                No sign-in events for this member in the current window.
              </div>
            ) : (
              <div className="rounded-md border border-border divide-y divide-border max-h-56 overflow-y-auto">
                {signins.map((s) => {
                  const out = OUTCOME_STYLE[s.outcome] || OUTCOME_STYLE.failure;
                  const Icon = out.icon;
                  return (
                    <div key={s.id} className="grid grid-cols-12 gap-2 items-center px-2.5 py-1.5 text-[12px]">
                      <div className="col-span-3">{fmtMDT(s.timestamp)}</div>
                      <div className="col-span-2">
                        <span className={cn("inline-flex items-center gap-1 rounded-full border text-[10px] font-semibold px-1.5 py-0.5 uppercase tracking-wide", out.cls)}>
                          <Icon className="w-2.5 h-2.5" /> {out.label}
                        </span>
                      </div>
                      <div className="col-span-4 truncate">{[s.city, s.country].filter(Boolean).join(", ") || "—"}</div>
                      <div className="col-span-3 truncate text-muted-foreground">{s.app}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Audit events for this member */}
          <section>
            <div className="text-[12.5px] font-semibold mb-1.5">Audit events ({audit.length})</div>
            {audit.length === 0 ? (
              <div className="rounded-md border border-dashed border-border p-4 text-center text-[12px] text-muted-foreground">
                No admin/vault/item audit events for this member.
              </div>
            ) : (
              <div className="rounded-md border border-border divide-y divide-border max-h-56 overflow-y-auto">
                {audit.map((a) => (
                  <div key={a.id} className="grid grid-cols-12 gap-2 items-center px-2.5 py-1.5 text-[12px]">
                    <div className="col-span-3">{fmtMDT(a.timestamp)}</div>
                    <div className="col-span-4">
                      <span className="inline-flex items-center rounded border border-border bg-muted/50 text-[10px] font-mono px-1.5 py-0.5">{a.action}</span>
                    </div>
                    <div className="col-span-3 truncate text-muted-foreground">{a.object_type}</div>
                    <div className="col-span-2 truncate text-muted-foreground">{a.country}</div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <DialogFooter className="px-5 py-3 border-t border-border">
          <a
            href={`https://team-unitedcloud.1password.com/people`}
            target="_blank"
            rel="noreferrer"
            className="text-[12px] text-primary hover:underline inline-flex items-center gap-1 mr-auto"
            data-testid="op-open-in-1password"
          >
            Open in 1Password admin <ChevronRight className="w-3 h-3" />
          </a>
          <Button variant="outline" onClick={onClose} data-testid="op-member-modal-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MiniStat({ label, value, tone = "primary" }) {
  const cls = {
    primary: "border-border bg-muted/30 text-foreground",
    rose:    "border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-300",
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300",
  }[tone];
  return (
    <div className={cn("rounded-lg border p-2.5", cls)}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
      <div className="text-[13px] font-semibold mt-0.5 truncate">{value}</div>
    </div>
  );
}
